import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ImSpinner8 } from 'react-icons/im';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { IoCloseOutline } from "react-icons/io5";
import subbox from "../../public/africfresh-box.jpeg";

const SubscriptionPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasOrderThisMonth, setHasOrderThisMonth] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionVisible, setSubscriptionVisible] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const navigate = useNavigate();
  const subscriptionRef = useRef(null);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      const userDetails = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
      const userEmail = userDetails?.email;
  
      if (!userEmail) return;
  
      try {
        const token = localStorage.getItem('AficFreshtoken');
        const response = await axios.get(
          `https://afric-fresh-backend.onrender.com/user/subscription-details?email=${userEmail}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        const subscription = response.data.subscription;
  
        console.log("Full subscription data:", subscription);
  
        const now = new Date();
        const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        console.log("Current month:", currentYearMonth);
  
        const hasOrderForCurrentMonth = subscription.some(sub =>
          sub.orders.some(order => order.orderMonth === currentYearMonth)
        );
  
        console.log("Has order for current month:", hasOrderForCurrentMonth);
  
        if (hasOrderForCurrentMonth) {
          setIsButtonDisabled(true);
          startCountdown();
        } else {
          setIsButtonDisabled(false);
        }
  
        const activeSubscription = subscription.find(sub => sub.status === 'active');
        if (activeSubscription) {
          setSubscriptionStatus(activeSubscription.type);
        }
        setSubscriptions(subscription);
        setLoading(false);
      } catch (error) {
        console.log('Error fetching subscription details:', error);
        setError(error.response?.data?.message || 'Failed to fetch subscriptions');
        setLoading(false);
      }
    };
  
    fetchSubscriptionDetails();
  }, []);

  const startCountdown = () => {
    const now = new Date();
    const houstonTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
    const nextMonth = new Date(houstonTime.getFullYear(), houstonTime.getMonth() + 1, 1);
    const timeRemaining = nextMonth - houstonTime;
  
    const interval = setInterval(() => {
      const now = new Date();
      const houstonTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
      const timeRemaining = nextMonth - houstonTime;
  
      if (timeRemaining <= 0) {
        clearInterval(interval);
        setIsButtonDisabled(false);
        setCountdown(null);
      } else {
        const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
  
        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);
  };

  // Modal functions
  const openModal = () => setIsModalOpen(true);
  const cancelModal = () => setIsModalOpen(false);

  const closeModal = async () => {
    const userDetails = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
    const userEmail = userDetails?.email;
  
    if (!userEmail) {
      Swal.fire({
        title: 'Oops!',
        html: `
          <div class="text-center">
            <p class="text-lg">You are not logged in. Please log in to proceed with the subscription plan.</p>
          </div>
        `,
        icon: 'warning',
        showCloseButton: true,
        closeButtonHtml: '✖',
        showConfirmButton: true,
        confirmButtonText: 'Go to Login',
        confirmButtonColor: '#007D44',
        showCancelButton: true,
        cancelButtonText: 'Close',
        cancelButtonColor: '#d33',
        timer: 40000,
        timerProgressBar: true,
        didOpen: () => {
          Swal.getPopup().style.transform = 'scale(0.8)';
          Swal.getPopup().style.transition = 'transform 0.3s ease';
          setTimeout(() => {
            Swal.getPopup().style.transform = 'scale(1)';
          }, 10);
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/auth/login');
        }
      });
      return;
    }
  
    try {
      setModalLoading(true);
      const token = localStorage.getItem('AficFreshtoken');
  
      const response = await axios.post(
        `https://afric-fresh-backend.onrender.com/user/send-subscription-email`, {
        email: userEmail,
        plan: selectedPlan
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
  
      const updatedUser = response.data.user;
      localStorage.setItem('AficFreshuserDetails', JSON.stringify(updatedUser));
  
      if (selectedPlan === 'large') {
        navigate('/largebox');
      } else if (selectedPlan === 'small') {
        navigate('/smallbox');
      }
  
      setIsModalOpen(false);
      setIsAccepted(false);
      setSelectedPlan(null);
    } catch (error) {
      console.log('Error sending subscription email:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to send subscription confirmation email. Please try again.',
        icon: 'error',
        confirmButtonColor: '#007D44',
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleSwitchPlan = (currentPlan) => {
    const newPlan = currentPlan === 'Large Box' ? 'Small Box' : 'Large Box';
    const token = localStorage.getItem('AficFreshtoken');
  
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to switch from ${currentPlan} to ${newPlan}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, switch!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userDetails = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
          const userEmail = userDetails?.email;
  
          Swal.fire({
            title: 'Switching Plan...',
            text: 'Please wait while we process your request.',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
  
          const response = await axios.put(
            'https://afric-fresh-backend.onrender.com/user/subscription/switch-plan',
            {
              email: userEmail,
              newPlan
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
  
          Swal.fire({
            title: 'Success!',
            text: `You have successfully switched to ${newPlan}.`,
            icon: 'success',
            confirmButtonText: `Go to ${newPlan}`,
            timer: 10000,
            timerProgressBar: true,
          }).then((result) => {
            if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
              navigate(newPlan === 'Large Box' ? '/largebox' : '/smallbox');
            }
          });
  
          // Refresh subscriptions
          const updatedSubscriptions = await axios.get(
            'https://afric-fresh-backend.onrender.com/user/subscription-details?email=' + userEmail,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          setSubscriptions(updatedSubscriptions.data.subscription);
          setSubscriptionStatus(newPlan);
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to switch plan', 'error');
        }
      }
    });
  };

  const handleCancelPlan = (currentPlan) => {
    const token = localStorage.getItem('AficFreshtoken');
  
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to cancel your ${currentPlan} subscription.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userDetails = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
          const userEmail = userDetails?.email;
  
          Swal.fire({
            title: 'Canceling Plan...',
            text: 'Please wait while we process your request.',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
  
          const response = await axios.put(
            'https://afric-fresh-backend.onrender.com/user/subscription/cancel-plan',
            {
              email: userEmail
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
  
          Swal.fire({
            title: 'Success!',
            text: `Your ${currentPlan} subscription has been canceled.`,
            icon: 'success',
            confirmButtonText: 'Go to Dashboard',
            timer: 10000,
            timerProgressBar: true,
          }).then((result) => {
            if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
              navigate('/');
            }
          });
  
          // Refresh subscriptions
          const updatedSubscriptions = await axios.get(
            'https://afric-fresh-backend.onrender.com/user/subscription-details?email=' + userEmail,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          setSubscriptions(updatedSubscriptions.data.subscription);
          setSubscriptionStatus(null);
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to cancel plan', 'error');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ImSpinner8 className="animate-spin text-4xl text-[#007D44]" />
      </div>
    );
  }

  if (error) return <p className="text-red-500 text-center mt-10">Error: {error}</p>;

  const activeSubscription = subscriptions.find((sub) => sub.status === 'active');
  const pastSubscriptions = subscriptions.filter((sub) => sub.status !== 'active');

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-center">Your Subscriptions</h1>

      {/* Subscription Plan Banner */}
      <div 
        ref={subscriptionRef}
        className={`
          flex flex-col md:flex-row items-center justify-between 
          bg-gradient-to-r from-white via-green-50 to-white
          p-6 md:p-8 rounded-xl shadow-xl my-10 mx-auto
          space-y-6 md:space-y-0 relative overflow-hidden
          transform transition-all duration-1000 ease-out
          ${subscriptionVisible 
            ? 'translate-y-0 opacity-100 scale-100' 
            : 'translate-y-12 opacity-0 scale-95'
          }
        `}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-green-200/30 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        {subscriptionStatus ? (
          <>
            <div className={`
              max-w-md text-center md:text-left relative z-10
              transform transition-all duration-700 ease-out
              ${subscriptionVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'}
            `}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 animate-pulse">
                You are already a {subscriptionStatus === 'Large Box' ? 'Large Box' : 'Small Box'} Subscriber!
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-2 leading-relaxed">
                Thank you for being a valued subscriber. As a {subscriptionStatus === 'Large Box' ? 'Large Box' : 'Small Box'} subscriber, you enjoy the convenience of fresh, authentic African groceries delivered to your door every month.
              </p>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                You can now proceed to select your items for this month. Simply click the button below to start customizing your box.
              </p>
              <button
                onClick={() => navigate(subscriptionStatus === 'Large Box' ? '/largebox' : '/smallbox')}
                disabled={isButtonDisabled}
                className={`
                  px-6 py-3 rounded-lg font-semibold text-lg max-sm:text-sm 
                  transition-all duration-300 transform
                  ${isButtonDisabled 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-[#007D44] to-[#005A32] hover:from-[#F0B241] hover:to-[#E09A2A] hover:scale-105 hover:shadow-lg active:scale-95'
                  } 
                  text-white shadow-md
                `}
              >
                {isButtonDisabled ? (
                  `Next order available in: ${countdown}`
                ) : (
                  `Go to ${subscriptionStatus === 'Large Box' ? 'Large Box' : 'Small Box'} Page`
                )}
              </button>
            </div>
            <div className={`
              flex-shrink-0 transform transition-all duration-700 ease-out
              ${subscriptionVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-8 opacity-0 scale-95'}
            `}>
              <img 
                src={subbox} 
                alt="Fresh Food Basket" 
                className="md:w-[450px] h-auto rounded-lg shadow-lg object-cover mx-auto md:mx-0 hover:scale-105 transition-transform duration-300" 
              />
            </div>
          </>
        ) : (
          <>
            <div className={`
              max-w-md text-center md:text-left relative z-10
              transform transition-all duration-700 ease-out
              ${subscriptionVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'}
            `}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-[#007D44] to-[#F0B241] bg-clip-text text-transparent">
                Food Subscription Plan
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                Enjoy fresh, authentic African groceries delivered to your door every month. Choose your subscription plan and experience the convenience of high-quality, flavorful ingredients right at home.
              </p>
              <button
                onClick={openModal}
                className="
                  px-6 py-3 bg-gradient-to-r from-[#007D44] to-[#005A32] text-white 
                  rounded-lg font-semibold text-lg 
                  hover:from-[#F0B241] hover:to-[#E09A2A] 
                  transform hover:scale-105 hover:shadow-lg 
                  active:scale-95 transition-all duration-300
                  shadow-md
                "
              >
                Subscribe Now
              </button>
            </div>

            <div className={`
              flex-shrink-0 transform transition-all duration-700 ease-out
              ${subscriptionVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-8 opacity-0 scale-95'}
            `}>
              <img 
                src={subbox} 
                alt="Fresh Food Basket" 
                className="w-64 md:w-[450px] h-auto rounded-lg shadow-lg object-cover mx-auto md:mx-0 hover:scale-105 transition-transform duration-300" 
              />
            </div>
          </>
        )}
      </div>

      {/* Active Subscription Details */}
      {activeSubscription && (
        <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4 text-[#007D44]">Active Subscription Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p><strong className="text-gray-700">Type:</strong> <span className="text-gray-900">{activeSubscription.type}</span></p>
              <p><strong className="text-gray-700">Start Date:</strong> <span className="text-gray-900">{new Date(activeSubscription.startDate).toLocaleDateString()}</span></p>
              <p><strong className="text-gray-700">Status:</strong> <span className="text-green-600 font-medium">{activeSubscription.status}</span></p>
            </div>
            <div>
              <p><strong className="text-gray-700">Total Orders:</strong> <span className="text-gray-900">{activeSubscription.orders.length}</span></p>
              <p><strong className="text-gray-700">Last Order:</strong> <span className="text-gray-900">
                {activeSubscription.orders.length > 0 
                  ? new Date(activeSubscription.orders[activeSubscription.orders.length - 1].orderDate).toLocaleDateString()
                  : 'No orders yet'}
              </span></p>
            </div>
          </div>
          
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => handleSwitchPlan(activeSubscription.type)}
              className="
                px-6 py-2 bg-[#007D44] text-white rounded-md
                hover:bg-[#005A32] transition-colors duration-300
                disabled:bg-gray-400 disabled:cursor-not-allowed
              "
              disabled={isButtonDisabled}
            >
              Switch Plan
            </button>
            <button
              onClick={() => handleCancelPlan(activeSubscription.type)}
              className="
                px-6 py-2 bg-red-500 text-white rounded-md
                hover:bg-red-600 transition-colors duration-300
                disabled:bg-gray-400 disabled:cursor-not-allowed
              "
              disabled={isButtonDisabled}
            >
              Cancel Plan
            </button>
            {isButtonDisabled && (
              <p className="text-red-500 mt-2 md:mt-0 md:ml-4 self-center">
                You can make changes in: <span className="font-bold">{countdown}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Past Subscriptions */}
      {pastSubscriptions.length > 0 && (
        <div className="mt-10">
          <h2 className="text-2xl font-semibold mb-6 text-[#007D44]">Subscription History</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pastSubscriptions.map((sub) => (
              <div key={sub._id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{sub.type}</h3>
                <p><strong className="text-gray-700">Start Date:</strong> <span className="text-gray-900">{new Date(sub.startDate).toLocaleDateString()}</span></p>
                <p><strong className="text-gray-700">End Date:</strong> <span className="text-gray-900">{sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'N/A'}</span></p>
                <p><strong className="text-gray-700">Status:</strong> <span className={sub.status === 'cancelled' ? 'text-red-500' : 'text-gray-900'}>{sub.status}</span></p>
                <p><strong className="text-gray-700">Total Orders:</strong> <span className="text-gray-900">{sub.orders.length}</span></p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="
            bg-white p-6 rounded-2xl shadow-2xl max-w-md mx-4 relative
            transform transition-all duration-500 ease-out
            animate-slideInScale
          ">
            {/* Close Icon */}
            <button
              onClick={cancelModal}
              className="
                absolute top-3 right-3 text-gray-400 hover:text-gray-600 
                transition-all duration-200 text-2xl
                hover:scale-110 hover:rotate-90 transform
              "
              aria-label="Close"
            >
              <IoCloseOutline />
            </button>
            
            <h3 className="text-2xl font-bold mb-4 text-center bg-gradient-to-r from-[#007D44] to-[#F0B241] bg-clip-text text-transparent">
              Subscribe to a Monthly Food Plan
            </h3>
            <p className="text-gray-700 mb-4 text-center leading-relaxed">
              Subscribe to a monthly food plan and enjoy the convenience of choosing your items each month. Pick any 10 items for $99 or any 6 items for $59, depending on your selected plan. Every month, you'll receive an email allowing you to select the items you want for that month.
            </p>

            <p className="text-gray-700 mb-4 text-center leading-relaxed">
              Items priced above $20 can only be selected once. Items priced between $10 and $20 can be selected up to twice. Items priced below $10 have no selection limit, allowing you to choose as many as you'd like.
            </p>

            <div className="flex items-center mb-4 group">
              <input
                type="checkbox"
                checked={isAccepted}
                onChange={() => setIsAccepted(!isAccepted)}
                className="mr-3 w-4 h-4 accent-[#007D44] transform transition-transform group-hover:scale-110"
              />
              <span className="text-gray-700">I accept the subscription terms and conditions</span>
            </div>

            {isAccepted && (
              <div className="animate-slideInUp">
                <div className="mt-4">
                  <label className="block text-gray-700 mb-2 font-semibold">Choose your box option:</label>
                  <select
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="
                      w-full px-3 py-2 border-2 border-gray-200 rounded-lg
                      focus:border-[#007D44] focus:ring-2 focus:ring-[#007D44]/20
                      transition-all duration-300
                    "
                  >
                    <option value="">Select a plan</option>
                    <option value="large">Large Box Plan - $99</option>
                    <option value="small">Small Box Plan - $59</option>
                  </select>
                </div>

                <button
                  onClick={closeModal}
                  className="
                    mt-6 w-full bg-gradient-to-r from-[#007D44] to-[#005A32] text-white 
                    px-4 py-3 rounded-lg font-semibold
                    hover:from-[#F0B241] hover:to-[#E09A2A] 
                    transform hover:scale-105 active:scale-95
                    transition-all duration-300 flex justify-center items-center
                    shadow-lg disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  disabled={!selectedPlan || modalLoading}
                >
                  {modalLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  ) : (
                    "Confirm Subscription"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional CSS for enhanced animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInScale {
          from { 
            opacity: 0; 
            transform: scale(0.8) translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideInScale { animation: slideInScale 0.5s ease-out; }
        .animate-slideInUp { animation: slideInUp 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default SubscriptionPage;